import { useEffect, useMemo, useRef, useState } from 'react';
import { resolveScopedOverride, type ComponentContentSchema, type SlotFieldOverride, type SlotGroupDefinition } from '@vibesites/runtime';
import { buildScopeContext, useGallery, usePage, useSlotGroup, useSlotGroupOverrides } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage, getMediaUrl, parseStorageKey, videoThumbStorageKey } from '@vibesites/ui-primitives';
import { galleryPhotoSrc, pickGalleryHeroPhoto } from '../utils/sortGallery';

const bgDef: SlotGroupDefinition = {
  label: 'Background Media',
  description:
    'Leads with the strongest community photo — exterior, clubhouse, pool, or another standout amenity. Set an image or video here to replace that choice. Otherwise the featured image is the fallback.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    video: {
      label: 'Video',
      type: 'video',
      defaultField: 'property_featured_video',
    },
  },
};

function fieldIsExplicit(field: SlotFieldOverride | null | undefined): boolean {
  if (!field?.type) return false;
  if (field.type === 'VALUE') return !!field.customValue?.trim();
  if (field.type === 'GENERATOR') return !!field.generatedValue?.trim();
  if (field.type === 'FIELD_MAPPING') return !!field.mappedField;
  return false;
}

export function GalleryHero() {
  const bg = useSlotGroup('galleryhero.bg_image', bgDef);
  const gallery = useGallery();
  const page = usePage();
  const slotGroupOverrides = useSlotGroupOverrides();
  const groupOverride = resolveScopedOverride(slotGroupOverrides, 'galleryhero.bg_image', buildScopeContext(page));
  const imageExplicit = fieldIsExplicit(groupOverride?.fieldOverrides?.image);
  const videoExplicit = fieldIsExplicit(groupOverride?.fieldOverrides?.video);

  const heroPhoto = useMemo(() => pickGalleryHeroPhoto(gallery), [gallery]);
  const curatedSrc = heroPhoto ? galleryPhotoSrc(heroPhoto) : '';
  // An image or video set on this hero wins. Otherwise lead with the strongest
  // community photo, then the property featured image.
  const displayImage = (imageExplicit ? bg.image : curatedSrc || bg.image) || '';
  const showVideo = videoExplicit && !!bg.video;
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster =
    (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || displayImage || '';

  const videoRef = useRef<HTMLVideoElement>(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setReducedMotion(mq.matches);
      if (mq.matches) videoRef.current?.pause();
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <section className="relative h-[42vh] min-h-[300px] max-h-[420px] overflow-hidden">
      <EditableSlotGroup groupPath="galleryhero.bg_image" definition={bgDef} className="absolute inset-0">
        {showVideo ? (
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            src={bg.video ?? undefined}
            poster={poster}
            autoPlay={!reducedMotion}
            muted
            loop
            playsInline
            aria-hidden
          />
        ) : displayImage ? (
          <VibesitesImage url={displayImage} alt="" sizes="100vw" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-[var(--vibe-color-text)]" />
        )}
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-black/30" />

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4" style={{ paddingTop: 'calc(var(--offer-banner-height, 0px) + 72px)' }}>
        <RichTextContent path="galleryhero.title" as="h1" className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal mb-3 uppercase tracking-wider" />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryHero',
  displayName: 'Gallery Hero',
  tokens: {
    'galleryhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Gallery</p>',
    },
  },
  slotGroups: {
    'galleryhero.bg_image': bgDef,
  },
};

(GalleryHero as any).contentSchema = contentSchema;

export default GalleryHero;
